import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { PlayCircle, FileText, HelpCircle, CheckCircle, ArrowLeft, ArrowRight, Download } from "lucide-react";
import DOMPurify from "dompurify";
import { toast } from "sonner";

export default function LessonView() {
  const { programId, lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    const parts = lessonId.split('_');
    const moduleId = parts.slice(0, -1).join('_');
    fetch(`${API}/programs/${programId}/modules/${moduleId}/lessons/${lessonId}`, { credentials: "include" })
      .then(r => { if (r.ok) return r.json(); throw new Error(); })
      .then(setLesson)
      .catch(() => toast.error("Error cargando la leccion"))
      .finally(() => setLoading(false));
  }, [programId, lessonId]);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const moduleId = lessonId.split('_').slice(0, -1).join('_');
      const res = await fetch(`${API}/programs/${programId}/modules/${moduleId}/lessons/${lessonId}/complete`, {
        method: "POST", credentials: "include"
      });
      if (res.ok) {
        toast.success("Leccion completada");
      }
    } catch {}
    setCompleting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-4 border-[#20D3C0] border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="lesson-view">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <button
          onClick={() => navigate(`/program/${programId}`)}
          className="flex items-center gap-2 text-[#193B6E] hover:text-[#20D3C0] mb-6 text-sm font-medium"
          data-testid="back-to-program"
        >
          <ArrowLeft className="w-4 h-4" /> Volver al programa
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video Player */}
            <div className="rounded-2xl overflow-hidden shadow-2xl bg-black border border-[#193B6E]/20 relative group video-protected" data-testid="video-player">
              {lesson?.video_embed ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(lesson.video_embed, { ADD_TAGS: ["iframe"], ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "src", "loading"] }) }} />
              ) : (
                <div className="aspect-video bg-gradient-to-br from-[#193B6E] to-[#112C54] flex items-center justify-center">
                  <div className="text-center text-white">
                    <PlayCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium opacity-75">Video de la leccion</p>
                    <p className="text-sm opacity-50 mt-1">El video sera embebido aqui desde BunnyCDN</p>
                  </div>
                </div>
              )}
            </div>

            {/* Lesson Info */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <h1 className="text-2xl font-bold text-[#193B6E] mb-2" style={{ fontFamily: 'Outfit' }} data-testid="lesson-title">
                {lesson?.title}
              </h1>
              <p className="text-slate-500 text-sm mb-4">{lesson?.description}</p>
              <div className="flex items-center gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-1"><PlayCircle className="w-4 h-4" /> {lesson?.duration_minutes} min</span>
              </div>
            </div>

            {/* PDF Section */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="pdf-section">
              <h3 className="text-lg font-semibold text-[#193B6E] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <FileText className="w-5 h-5 text-[#20D3C0]" /> Material PDF
              </h3>
              {lesson?.pdf_url ? (
                <div className="pdf-viewer border border-slate-200 rounded-lg overflow-hidden" style={{ height: '500px' }}>
                  <iframe
                    src={`${lesson.pdf_url}#toolbar=0&navpanes=0`}
                    className="w-full h-full"
                    title="Material PDF"
                  />
                </div>
              ) : (
                <div className="bg-[#F8FAFC] rounded-lg p-8 text-center border border-slate-100">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-400 text-sm">El material PDF sera agregado proximamente</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm space-y-3">
              <button
                onClick={handleComplete}
                disabled={completing}
                className="w-full bg-[#20D3C0] text-[#112C54] hover:bg-[#1AB8A6] font-semibold rounded-xl px-4 py-3 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="complete-lesson-btn"
              >
                <CheckCircle className="w-5 h-5" />
                {completing ? "Marcando..." : "Marcar como completada"}
              </button>

              {lesson?.quiz_id && (
                <Link
                  to={`/quiz/${lesson.quiz_id}`}
                  className="w-full bg-[#193B6E] text-white hover:bg-[#132D55] font-semibold rounded-xl px-4 py-3 transition-all flex items-center justify-center gap-2"
                  data-testid="take-quiz-btn"
                >
                  <HelpCircle className="w-5 h-5" />
                  Hacer cuestionario
                </Link>
              )}
            </div>

            {/* Ad placeholder for free users */}
            <div className="ad-placeholder p-6 rounded-xl" data-testid="ad-placeholder">
              <div className="text-center">
                <p className="text-slate-400 text-xs">Espacio publicitario</p>
                <p className="text-slate-300 text-xs mt-1">728x90</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
